/**
 * Which version this is, where a person can see it.
 *
 * Bundle hashes did this job and did it badly. Nobody holds main.795f9167.js
 * in their head, and it appears nowhere on screen, so "did my install land"
 * turned into an investigation twice in one week. Both times the answer was
 * that the build being tested was not the build that had been made.
 *
 * The short stamp lives under the sidebar and is always in view. The details
 * live in settings and take the two halves apart, because the frontend and
 * the backend are built together and shipped along different paths: the phone
 * loads its screen from the connect service and tunnels its API calls to the
 * Mac, so those two can be different versions and that is exactly the thing
 * worth noticing.
 */

import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { VERSION, BUILT_AT } from "../build-info";
import { useTaal } from "@/contexts/TaalContext";

/** The filename the browser actually loaded, for when the version is not enough. */
export function loadedBundle() {
  const tag = document.querySelector('script[src*="/static/js/main."]');
  if (!tag) return "";
  const match = tag.getAttribute("src").match(/main\.[a-z0-9]+\.js/);
  return match ? match[0] : "";
}

export function BuildStamp({ className = "" }) {
  return (
    <p className={`text-[10px] text-muted-foreground text-center ${className}`} data-testid="build-stamp">
      v{VERSION} · {BUILT_AT}
    </p>
  );
}

export function BuildDetails({ bundle }) {
  const { t } = useTaal();
  const [backend, setBackend] = useState(null);
  const [reachable, setReachable] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/version`);
        if (!cancelled) { setBackend(res.data); setReachable(true); }
      } catch {
        if (!cancelled) { setBackend(null); setReachable(false); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const shown = bundle !== undefined ? bundle : loadedBundle();
  const drifted = reachable && backend && backend.version !== VERSION;

  return (
    <div className="space-y-1 text-xs text-muted-foreground">
      <div className="flex justify-between gap-4" data-testid="build-details-frontend">
        <span>{t("App")}</span>
        <span className="font-mono">v{VERSION} · {BUILT_AT}</span>
      </div>
      <div className="flex justify-between gap-4" data-testid="build-details-backend">
        <span>{t("Backend")}</span>
        <span className="font-mono">
          {reachable ? (backend ? `v${backend.version} · ${backend.built_at}` : "…") : t("antwoordt niet")}
        </span>
      </div>
      {shown && (
        <div className="flex justify-between gap-4" data-testid="build-details-bundle">
          <span>{t("Bundel")}</span>
          <span className="font-mono truncate">{shown}</span>
        </div>
      )}
      {drifted && (
        <p className="text-destructive pt-1" data-testid="build-details-mismatch">
          {t("De app en de backend hebben verschillende versies. Op een telefoon betekent dit dat de gekoppelde machine nog niet is bijgewerkt.")}
        </p>
      )}
    </div>
  );
}

export default BuildStamp;
